import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import ArcadeNav from "../arcade/ArcadeNav";
import ArcadeFooter from "../arcade/ArcadeFooter";
import useSEO from "../seo/useSEO";
import "./legal.css";

const CONTACT_EMAIL = "hello@zombiecollector3d.com";
const LAST_UPDATED = "June 26, 2026";

export default function PrivacyPage() {
  useSEO({
    title: "Privacy Policy — Zombie Arcade",
    description:
      "How Zombie Arcade collects, uses, and protects your information across Zombie Collector 3D, Zombie Solitaire, Zombie Crush, Zombie Merge, and Zombie Idle Tycoon.",
    canonical: "https://zombiecollector3d.com/privacy",
  });

  return (
    <div className="legal-page" data-testid="privacy-page">
      <ArcadeNav />
      <main className="legal-main">
        <Link to="/" className="legal-back" data-testid="privacy-back">
          <ArrowLeft size={16} /> Back to Arcade
        </Link>

        <header className="legal-hero">
          <h1>Privacy Policy</h1>
          <p className="subtitle">
            What we collect, why, and how you can control it. Written in
            plain English because you deserve to understand it.
          </p>
          <p className="legal-updated">Last updated: {LAST_UPDATED}</p>
        </header>

        <section className="legal-section">
          <h2>The short version</h2>
          <p>
            Zombie Arcade is a free browser-based collection of five HTML5
            games. We don&apos;t sell your data. We collect the minimum needed to
            let you sign in, save your progress across devices, buy a VIP
            pass if you want one, and keep the leaderboards honest. If you
            want everything deleted, email us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we&apos;ll
            wipe your account within 7 days.
          </p>
        </section>

        <section className="legal-section">
          <h2>1. Who we are</h2>
          <p>
            &quot;Zombie Arcade&quot; (the &quot;Site&quot;, &quot;we&quot;, &quot;us&quot;) operates the website{" "}
            <strong>zombiecollector3d.com</strong> and its subdomains. This
            Privacy Policy applies to every game hosted on the Site,
            including Zombie Collector 3D, Zombie Solitaire, Zombie Crush,
            Zombie Merge, and Zombie Idle Tycoon. Contact us any time at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Information we collect</h2>
          <h3>2.1 When you play as a guest</h3>
          <p>
            You can play every game without signing in. In that case we
            store your progress in your own browser using <strong>localStorage</strong>{" "}
            (e.g. your best Solitaire time, your Zombie Idle Tycoon save,
            your Crush high score). We do not upload that data to any
            server. Clearing your browser data will erase this progress.
          </p>
          <h3>2.2 When you sign in with Google</h3>
          <p>
            If you choose to sign in with Google, we receive from Google
            only the information needed to identify your account: your
            Google account email address, your display name, your profile
            picture URL, and a stable account identifier. We never receive
            your Google password.
          </p>
          <h3>2.3 When you play while signed in</h3>
          <p>
            We store gameplay progress on our servers so it follows you
            across devices. This includes your player level, XP, coins,
            gems, cosmetics owned, VIP status, achievements, run stats,
            and the results of finished games (score, level, moves used,
            elapsed time, and whether you won).
          </p>
          <h3>2.4 When you make a purchase</h3>
          <p>
            VIP passes and coin packs are processed by <strong>Stripe</strong>. We never
            see or store your full credit card number. Stripe returns to
            us only a session identifier and success/failure status. See{" "}
            <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
              Stripe&apos;s Privacy Policy
            </a>{" "}
            for how they handle payment data.
          </p>
          <h3>2.5 Automatically collected</h3>
          <p>
            When you visit any page we may automatically log your IP
            address, browser type and version, referring URL, and pages
            visited &mdash; this is standard web-server behaviour and is
            used for security, abuse prevention, and aggregate analytics
            (e.g. &quot;how many people played Solitaire this week&quot;). We do not
            build advertising profiles from this data.
          </p>
        </section>

        <section className="legal-section">
          <h2>3. How we use your information</h2>
          <ul>
            <li>To let you sign in and stay signed in across sessions.</li>
            <li>To sync your game progress across devices.</li>
            <li>To display your username and level on leaderboards.</li>
            <li>To grant your VIP / Founder perks after a successful purchase.</li>
            <li>To prevent abuse (rate-limits, fraud detection on rewards).</li>
            <li>To reply to your emails when you contact support.</li>
            <li>To send critical service emails (e.g. VIP receipt). We do <strong>not</strong> send marketing emails.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Cookies and local storage</h2>
          <p>
            Zombie Arcade uses <strong>first-party cookies</strong> to keep you signed in
            (a JWT session token) and <strong>localStorage</strong> to save game state
            client-side (Tycoon save, Crush high score, Solitaire streak,
            etc.). We do not use third-party advertising cookies. If we
            enable ads in the future via Google&apos;s H5 Games Ads product
            (which we have been approved for but not yet enabled), Google
            may set additional cookies subject to{" "}
            <a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer">
              Google&apos;s Cookies Policy
            </a>, and we will update this Privacy Policy before turning
            ads on.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. Third-party services we use</h2>
          <ul>
            <li>
              <strong>Google (Sign-In with Google)</strong> &mdash; used only for
              authentication.{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                Google Privacy Policy
              </a>
            </li>
            <li>
              <strong>Stripe</strong> &mdash; used only for payment processing.{" "}
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
                Stripe Privacy Policy
              </a>
            </li>
            <li>
              <strong>MongoDB Atlas</strong> &mdash; hosts our player database in the
              United States.
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>6. Your rights</h2>
          <p>
            No matter where you live, you can email us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> to:
          </p>
          <ul>
            <li>Ask what data we hold on you (data-access request).</li>
            <li>Correct anything that&apos;s wrong.</li>
            <li>Delete your account and all associated data (data-erasure request).</li>
            <li>Export a copy of your data in JSON format.</li>
            <li>Withdraw consent to future marketing (we don&apos;t send any today).</li>
          </ul>
          <p>
            We&apos;ll respond within <strong>7 days</strong>. Residents of California, the
            EEA, and the UK have additional rights under the CCPA, GDPR, and
            UK GDPR respectively &mdash; those laws apply to us and we honour
            those requests on the same 7-day timeline.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Children</h2>
          <p>
            Zombie Arcade is designed for a general audience and is not
            directed at children under 13. We do not knowingly collect
            personal information from children under 13. If you are a
            parent or guardian and believe your child has provided us with
            personal information, please contact us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we
            will delete the account promptly.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Data retention and security</h2>
          <p>
            We keep your account data as long as your account is active.
            Deleted accounts are purged within 30 days. Payment records
            required for tax purposes are kept for 7 years per US federal
            record-keeping requirements. All data is transmitted over
            HTTPS; passwords for internal admin accounts are hashed with
            bcrypt; JWT tokens use HTTP-only cookies with the{" "}
            <code>secure</code> and <code>SameSite=Lax</code> flags.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Changes to this policy</h2>
          <p>
            If we make material changes to this policy, we&apos;ll update the
            &quot;Last updated&quot; date at the top and, if you have an account,
            we&apos;ll show you a notice the next time you sign in. Continued
            use after a change means you accept the new policy.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Contact</h2>
          <p>
            Questions, requests, complaints, or love-letters:{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </p>
        </section>

        <ArcadeFooter />
      </main>
    </div>
  );
}
